/**
 * Trilingual UI strings used by the standardized actor avatar / actor
 * name primitives (`<ActorAvatar>`, `<ActorName>`).
 *
 * Mirrors the pattern of `storyUi.ts`. Keys cover the screen-reader and
 * tooltip surface area only — visible product copy comes from the
 * higher-level features (chat, profile, etc.).
 */
export type ActorLocale = "en-US" | "ja-JP" | "ko-KR";

interface ActorStrings {
  /** aria-label when the avatar opens the actor's story. `{name}` is the display name. */
  viewStoryAria: string;
  /** aria-label when the avatar opens the actor's profile. `{name}` is the display name. */
  openProfileAria: string;
  /** Screen-reader description for an unseen story ring. */
  storyRingUnseen: string;
  /** Screen-reader description for a viewed story ring. */
  storyRingSeen: string;
  /** Alt text for the avatar image. `{name}` is the display name. */
  avatarAlt: string;
  /** Aria-label for `<ActorName>` links. `{name}` is the display name. */
  openProfileNameAria: string;
}

const STRINGS: Record<ActorLocale, ActorStrings> = {
  "en-US": {
    viewStoryAria: "View {name}'s story",
    openProfileAria: "Open {name}'s profile",
    storyRingUnseen: "{name} has new stories",
    storyRingSeen: "{name}'s stories",
    avatarAlt: "{name}'s avatar",
    openProfileNameAria: "Open {name}'s profile",
  },
  "ja-JP": {
    viewStoryAria: "{name}さんのストーリーを見る",
    openProfileAria: "{name}さんのプロフィールを開く",
    storyRingUnseen: "{name}さんに新しいストーリーがあります",
    storyRingSeen: "{name}さんのストーリー",
    avatarAlt: "{name}さんのアバター",
    openProfileNameAria: "{name}さんのプロフィールを開く",
  },
  "ko-KR": {
    viewStoryAria: "{name}님의 스토리 보기",
    openProfileAria: "{name}님의 프로필 열기",
    storyRingUnseen: "{name}님에게 새 스토리가 있습니다",
    storyRingSeen: "{name}님의 스토리",
    avatarAlt: "{name}님의 아바타",
    openProfileNameAria: "{name}님의 프로필 열기",
  },
};

/** Resolves the active locale from the browser. */
export function resolveActorLocale(): ActorLocale {
  if (typeof navigator === "undefined" || !navigator.language) {
    return "en-US";
  }
  const lang = navigator.language.toLowerCase();
  if (lang.startsWith("ja")) {
    return "ja-JP";
  }
  if (lang.startsWith("ko")) {
    return "ko-KR";
  }
  return "en-US";
}

/**
 * Return the string bundle for the given locale (defaults to the
 * browser locale).
 */
export function getActorUiStrings(locale?: ActorLocale): ActorStrings {
  const key = locale ?? resolveActorLocale();
  return STRINGS[key] ?? STRINGS["en-US"];
}

/**
 * Tiny ICU-style {name} interpolator. Avoids pulling a full ICU runtime
 * for the handful of one-arg messages used here.
 */
export function formatActorString(template: string, name: string): string {
  return template.replace(/\{name\}/g, name);
}
